import { Worksheet } from "exceljs";
import { eSortMode, IColumn, ICondition, IExpression } from "./DataTable";

export const removeAccents = (str: string) => {
    if (!str) return str;
    return str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/đ/g, "d")
        .replace(/Đ/g, "D");
}
export const dynamicSort = (property: string, mode: eSortMode) => {
    var sortOrder = 1;
    if (mode === eSortMode.ASC) {
        sortOrder = 1;
    } else {
        sortOrder = -1;
    }
    return function (a: any, b: any) {
        var result =
            a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
        return result * sortOrder;
    };
}
export const handleSearch = (columns: IColumn[], data: any[], key: string): any[] => {
    const normalizedKey = removeAccents(key.toLowerCase());
    const searchableColumns = columns.filter(col => col.dataField);
    return data.filter(item =>
        searchableColumns.some(col => {
            const value = item[`${col.dataField}`];
            return value &&
                removeAccents(value.toString().toLowerCase())
                    .includes(normalizedKey);
        })
    );
};
export const handleFilter = (conditions: ICondition[], data: any[], logicOperator: string): any[] => {
    const filterExpression = conditions.map((cond: any) => [cond.field, cond.operator, cond.value])
        .reduce((acc: any, curr: any, i: any) => (i > 0 ? [...acc, logicOperator, curr] : [curr]), []);
    return data.filter((item) => {
        let result = true;
        let currentOperator = "and";

        for (let i = 0; i < filterExpression.length; i++) {
            const expr = filterExpression[i];
            if (typeof expr === "string") {
                currentOperator = expr;
                continue;
            }
            const [field, operator, value]: IExpression = expr;
            const itemValue = item[field];

            let conditionResult;
            switch (operator) {
                case "equals":
                    conditionResult = itemValue === value;
                    break;
                case "contains":
                    conditionResult = String(itemValue).toLowerCase().includes(String(value).toLowerCase());
                    break;
                case "greaterThan":
                    conditionResult = Number(itemValue) > Number(value);
                    break;
                case "lessThan":
                    conditionResult = Number(itemValue) < Number(value);
                    break;
                case "greaterThanOrEqual":
                    conditionResult = Number(itemValue) >= Number(value);
                    break;
                case "lessThanOrEqual":
                    conditionResult = Number(itemValue) <= Number(value);
                    break;
                default:
                    conditionResult = true;
            }
            result = currentOperator === "and" ? result && conditionResult : result || conditionResult;
        }
        return result;
    });
};
export interface IDataGridHeaderRow {
  rowIdx: number;
  cells: IDataGridHeaderCell[];
}
export interface IDataGridHeaderCell extends IColumn {
  rowspan: number;
  colspan: number;
  level: number;
}
export const GetDataGridHederGroupCell = (columns: IColumn[]) => {
  const result = buildHeaderRows(columns);
  return result;
};
export function buildHeaderRows(
  columns: IColumn[],
): IDataGridHeaderRow[] {
  const maxLevel = getMaxDepth(columns);
  const rows: IDataGridHeaderRow[] = [];

  function traverse(cols: IColumn[], level: number) {
    rows[level - 1] ??= {
      rowIdx: level - 1,
      cells: [],
    };

    cols.forEach((col) => {
      const isGroup = !!col.columns?.length;

      const cell: IDataGridHeaderCell = {
        ...col,
        level,
        colspan: isGroup ? countLeafColumns(col) : 1,
        rowspan: isGroup ? 1 : maxLevel - level + 1,
      };

      rows[level - 1].cells.push(cell);

      if (isGroup) {
        traverse(col.columns!, level + 1);
      }
    });
  }

  traverse(columns, 1);
  return rows;
}
const getMaxDepth = (columns: IColumn[], depth = 1): number => {
  return Math.max(
    ...columns.map((col) =>
      col.columns?.length ? getMaxDepth(col.columns, depth + 1) : depth,
    ),
  );
};
const countLeafColumns = (column: IColumn): number => {
  if (!column.columns || column.columns.length === 0) {
    return 1;
  }

  return column.columns.reduce((sum, col) => sum + countLeafColumns(col), 0);
};


export const renderExcelHeader = (
  worksheet: Worksheet,
  headerRows: IDataGridHeaderRow[],
  startRow = 1,
  startCol = 1,
) => {
  const occupied = new Map<string, boolean>();

  const isOccupied = (r: number, c: number) =>
    occupied.get(`${r}_${c}`) === true;

  const markOccupied = (r1: number, c1: number, r2: number, c2: number) => {
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        occupied.set(`${r}_${c}`, true);
      }
    }
  };

  headerRows.forEach((headerRow, rowOffset) => {
    const row = startRow + rowOffset;
    let col = startCol;

    headerRow.cells.forEach((cell) => {
      // 🔑 tìm cột trống tiếp theo
      while (isOccupied(row, col)) {
        col++;
      }

      const excelCell = worksheet.getCell(row, col);
      excelCell.value = cell.caption ?? cell.dataField?.toString() ?? "";

      excelCell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };

      excelCell.font = { bold: true };
      excelCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      const r2 = row + cell.rowspan - 1;
      const c2 = col + cell.colspan - 1;

      if (cell.rowspan > 1 || cell.colspan > 1) {
        worksheet.mergeCells(row, col, r2, c2);
      }

      // 🔑 đánh dấu vùng đã chiếm
      markOccupied(row, col, r2, c2);

      col += cell.colspan;
    });
  });
};
